import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const CommentFieldsConfig = () => {
  const [commentFields, setCommentFields] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validation schemas
  const fieldSchema = Yup.object().shape({
    field_name: Yup.string().required('Field name is required'),
    field_code: Yup.string()
      .required('Field code is required')
      .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed'),
    display_name: Yup.string().required('Display name is required'),
    field_type_id: Yup.number()
      .required('Field type is required')
      .min(1, 'Please select a field type'),
    max_score: Yup.number()
      .required('Max score is required')
      .min(1, 'Must be at least 1')
      .max(10, 'Cannot exceed 10'),
    min_score: Yup.number()
      .required('Min score is required')
      .min(0, 'Must be at least 0')
      .max(Yup.ref('max_score'), 'Cannot exceed max score'),
    display_order: Yup.number().required('Display order is required'),
  });

  const typeSchema = Yup.object().shape({
    name: Yup.string().required('Type name is required').max(50, 'Maximum 50 characters'),
    description: Yup.string().max(255, 'Maximum 255 characters'),
  });

  // Data fetching
  const fetchData = async () => {
    try {
      setLoading(true);
      const [fieldsRes, typesRes] = await Promise.all([
        adminAPI.getCommentFields(),
        adminAPI.getCommentFieldTypes(),
      ]);
      setCommentFields(fieldsRes.data || []);
      setFieldTypes(typesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handlers for comment fields
  const handleSaveField = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingField) {
        await adminAPI.updateCommentField(editingField.id, values);
        toast.success('Field updated successfully');
      } else {
        await adminAPI.createCommentField(values);
        toast.success('Field created successfully');
      }
      setShowFieldModal(false);
      resetForm();
      setEditingField(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteField = async (id) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await adminAPI.deleteCommentField(id);
        toast.success('Field deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete field');
      }
    }
  };

  // Handlers for field types
  const handleSaveType = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingType) {
        await adminAPI.updateCommentFieldType(editingType.id, values);
        toast.success('Type updated successfully');
      } else {
        await adminAPI.createCommentFieldType(values);
        toast.success('Type created successfully');
      }
      setShowTypeModal(false);
      resetForm();
      setEditingType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm('Are you sure you want to delete this field type?')) {
      try {
        await adminAPI.deleteCommentFieldType(id);
        toast.success('Type deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete type');
      }
    }
  };

  const getFieldTypeBadge = (typeName) => {
    const colors = {
      psychomotor: 'bg-blue-100 text-blue-800',
      affective: 'bg-green-100 text-green-800',
      custom: 'bg-purple-100 text-purple-800',
    };
    return colors[typeName] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Comment Fields Configuration</h1>
          <p className="text-gray-600">Configure dynamic comment and rating fields for form masters</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setEditingType(null);
              setShowTypeModal(true);
            }}
            className="btn-secondary flex items-center"
          >
            <i className="fas fa-tags mr-2"></i> Manage Types
          </button>
          <button
            onClick={() => {
              if (fieldTypes.length === 0) {
                toast.error('Please create at least one field type first');
                setShowTypeModal(true);
                return;
              }
              setEditingField(null);
              setShowFieldModal(true);
            }}
            className="btn-primary flex items-center"
          >
            <i className="fas fa-plus mr-2"></i> Add Field
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Fields</p>
              <p className="text-2xl font-bold">{commentFields.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-comment text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Field Types</p>
              <p className="text-2xl font-bold">{fieldTypes.length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-tags text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Fields</p>
              <p className="text-2xl font-bold">
                {commentFields.filter(f => f.is_active).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Field Types List (if any) */}
      {fieldTypes.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-700">Available Field Types</h4>
            <button
              onClick={() => {
                setEditingType(null);
                setShowTypeModal(true);
              }}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              <i className="fas fa-plus mr-1"></i> Add Type
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {fieldTypes.map(type => (
              <span
                key={type.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
              >
                {type.name}
                <button
                  onClick={() => {
                    setEditingType(type);
                    setShowTypeModal(true);
                  }}
                  className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  onClick={() => handleDeleteType(type.id)}
                  className="ml-1 text-xs text-red-500 hover:text-red-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* No Types Warning */}
      {fieldTypes.length === 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle text-yellow-400 text-xl"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                No field types defined yet. Please create at least one field type before adding comment fields.
              </p>
              <button
                onClick={() => {
                  setEditingType(null);
                  setShowTypeModal(true);
                }}
                className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
              >
                <i className="fas fa-plus mr-1"></i> Create First Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Fields Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Comment Fields</h3>
          <p className="text-gray-600 text-sm">Manage dynamic rating fields for student comments</p>
        </div>
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scores & Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {commentFields.map((field) => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {field.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {field.field_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFieldTypeBadge(field.field_type_name)}`}>
                        {field.field_type_name}
                      </span>
                      <div className="text-sm text-gray-600 font-mono">
                        {field.field_code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-gray-500">Score Range: </span>
                        <span className="font-medium">
                          {field.min_score} - {field.max_score}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Display Order: </span>
                        <span className="font-medium">{field.display_order}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      field.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {field.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        setEditingField(field);
                        setShowFieldModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====== Modal: Create/Edit Comment Field ====== */}
      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        title={editingField ? 'Edit Comment Field' : 'Create Comment Field'}
        size="lg"
      >
        <Formik
          initialValues={{
            field_name: editingField?.field_name || '',
            field_code: editingField?.field_code || '',
            display_name: editingField?.display_name || '',
            field_type_id: editingField?.field_type_id || '',
            max_score: editingField?.max_score || 5,
            min_score: editingField?.min_score || 1,
            display_order: editingField?.display_order || 0,
            is_active: editingField?.is_active ?? true,
          }}
          validationSchema={fieldSchema}
          onSubmit={handleSaveField}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Name *
                  </label>
                  <Field
                    type="text"
                    name="field_name"
                    className="input-field"
                    placeholder="e.g., Attendance Rating"
                  />
                  {errors.field_name && touched.field_name && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_name}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Code *
                  </label>
                  <Field
                    type="text"
                    name="field_code"
                    className="input-field font-mono"
                    placeholder="e.g., attendance"
                  />
                  {errors.field_code && touched.field_code && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_code}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Lowercase letters, numbers, and underscores only
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <Field
                  type="text"
                  name="display_name"
                  className="input-field"
                  placeholder="e.g., Attendance"
                />
                {errors.display_name && touched.display_name && (
                  <div className="text-red-500 text-sm mt-1">{errors.display_name}</div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Type *
                  </label>
                  <Field
                    as="select"
                    name="field_type_id"
                    className="input-field"
                  >
                    <option value="">Select a type</option>
                    {fieldTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Field>
                  {errors.field_type_id && touched.field_type_id && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_type_id}</div>
                  )}
                  {fieldTypes.length === 0 && (
                    <p className="text-xs text-yellow-600 mt-1">
                      No types available. Please create a type first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order *
                  </label>
                  <Field
                    type="number"
                    name="display_order"
                    className="input-field"
                    min="0"
                  />
                  {errors.display_order && touched.display_order && (
                    <div className="text-red-500 text-sm mt-1">{errors.display_order}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Score *
                  </label>
                  <Field
                    type="number"
                    name="min_score"
                    className="input-field"
                    min="0"
                    max={values.max_score}
                  />
                  {errors.min_score && touched.min_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.min_score}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Score *
                  </label>
                  <Field
                    type="number"
                    name="max_score"
                    className="input-field"
                    min={values.min_score}
                    max="10"
                  />
                  {errors.max_score && touched.max_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.max_score}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <Field
                  type="checkbox"
                  name="is_active"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active Field
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFieldModal(false);
                    setEditingField(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || fieldTypes.length === 0}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingField ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* ====== Modal: Manage Field Types ====== */}
      <Modal
        isOpen={showTypeModal}
        onClose={() => {
          setShowTypeModal(false);
          setEditingType(null);
        }}
        title={editingType ? 'Edit Field Type' : 'Create Field Type'}
        size="md"
      >
        <Formik
          initialValues={{
            name: editingType?.name || '',
            description: editingType?.description || '',
          }}
          validationSchema={typeSchema}
          onSubmit={handleSaveType}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type Name *
                </label>
                <Field
                  type="text"
                  name="name"
                  className="input-field"
                  placeholder="e.g., Psychomotor"
                />
                {errors.name && touched.name && (
                  <div className="text-red-500 text-sm mt-1">{errors.name}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <Field
                  as="textarea"
                  name="description"
                  className="input-field"
                  rows="2"
                  placeholder="Brief description of this field type"
                />
                {errors.description && touched.description && (
                  <div className="text-red-500 text-sm mt-1">{errors.description}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTypeModal(false);
                    setEditingType(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingType ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default CommentFieldsConfig;